import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import IceShowPlayer from '../components/IceShowPlayer';
import MenuOverlay from '../components/MenuOverlay';
import PROJECTS from '../data/projects';

gsap.registerPlugin(ScrollTrigger);

const ProjectDetail = () => {
  const { projectId } = useParams();
  const project = PROJECTS.find((p) => p.id === projectId);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const textRef = useRef(null);

  // Redirect if project not found
  useEffect(() => {
    if (!project) navigate('/work', { replace: true });
  }, [project, navigate]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Enable scrolling on this page
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    html.style.overflow = 'auto';
    html.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.height = 'auto';
    body.style.position = 'static';
    if (root) {
      root.style.overflow = 'auto';
      root.style.height = 'auto';
      root.style.position = 'static';
    }

    return () => {
      html.style.overflow = 'hidden';
      html.style.height = '100%';
      body.style.overflow = 'hidden';
      body.style.height = '100%';
      body.style.position = 'fixed';
      if (root) {
        root.style.overflow = 'hidden';
        root.style.height = '100%';
        root.style.position = 'fixed';
      }
    };
  }, []);

  // GSAP scroll-triggered text reveal animation — desktop only
  useEffect(() => {
    if (!textRef.current || isMobile || !project) return;

    const text = textRef.current.innerText;
    const words = text.split(' ');

    textRef.current.innerHTML = words
      .map((word, i) => `<span class="measure-word" data-index="${i}">${word}</span>`)
      .join(' ');

    const timer = setTimeout(() => {
      const wordSpans = Array.from(textRef.current.querySelectorAll('.measure-word'));
      const lines = [];
      let currentLine = [];
      let lastTop = null;

      wordSpans.forEach((span) => {
        const top = span.getBoundingClientRect().top;
        if (lastTop === null || Math.abs(top - lastTop) < 5) {
          currentLine.push(span.textContent);
        } else {
          if (currentLine.length > 0) lines.push(currentLine.join(' '));
          currentLine = [span.textContent];
        }
        lastTop = top;
      });
      if (currentLine.length > 0) lines.push(currentLine.join(' '));

      textRef.current.innerHTML = lines
        .map(
          (line) =>
            `<div class="reveal-line" style="
              background: linear-gradient(to right, rgb(209, 213, 219) 50%, rgb(55, 55, 55) 50%);
              background-size: 200% 100%;
              background-position-x: 100%;
              color: transparent;
              background-clip: text;
              -webkit-background-clip: text;
              line-height: 2.2;
              margin-bottom: 0.4em;
            ">${line}</div>`
        )
        .join('');

      const lineElements = textRef.current.querySelectorAll('.reveal-line');

      lineElements.forEach((line) => {
        gsap.to(line, {
          backgroundPositionX: '0%',
          ease: 'none',
          scrollTrigger: {
            trigger: line,
            scrub: 0.5,
            start: 'top 98%',
            end: 'top 85%',
          },
        });
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [isMobile, project]);

  if (!project) return null;

  return (
    <div className="bg-black">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-20 p-8 md:p-10 lg:p-10">
        <div className="flex items-center justify-between w-full">
          <div className="w-20"></div>
          <div className="flex-1 flex justify-center">
            <h1
              onClick={() => navigate('/')}
              className="text-white text-lg md:text-xl lg:text-2xl font-light tracking-widest uppercase cursor-pointer"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              Joshua Gavin
            </h1>
          </div>
          <button
            onClick={() => setIsMenuOpen(true)}
            className="w-20 flex flex-col items-end gap-2 bg-transparent border-none cursor-pointer p-0 group"
            aria-label="Open menu"
          >
            <span
              className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12"
              style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
            ></span>
            <span
              className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12"
              style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
            ></span>
            {isMobile && (
              <span
                className="w-8 h-0.5 bg-white block transition-all duration-300 group-hover:w-12"
                style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
              ></span>
            )}
          </button>
        </div>
      </nav>

      {/* Video Player */}
      <div className="w-full">
        {isMobile && (
          <div
            className="w-full h-20"
            style={{
              background:
                'linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
            }}
          />
        )}
        <IceShowPlayer
          playbackId={project.videoPlaybackId}
          title={project.videoTitle}
        />
      </div>

      {/* Details */}
      <div className="bg-black px-4 md:px-16 lg:px-24 pt-8 md:pt-20 pb-16 md:pb-32">
        <div className="max-w-full">
          <h2
            className="text-white text-xl md:text-3xl font-bold uppercase tracking-wider mb-4 md:mb-10"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {project.detailHeading}
          </h2>
          {isMobile ? (
            <>
              {project.paragraphs.map((text, i) => (
                <p
                  key={i}
                  className="text-sm md:text-lg leading-relaxed text-gray-300 mb-4"
                  style={{ fontFamily: 'Montserrat, sans-serif', lineHeight: 1.6 }}
                >
                  {text}
                </p>
              ))}
            </>
          ) : (
            <p
              ref={textRef}
              className="text-lg leading-relaxed text-gray-300"
              style={{ fontFamily: 'Montserrat, sans-serif', lineHeight: 1.8 }}
              dangerouslySetInnerHTML={{
                __html: project.paragraphs.join('<br /><br />'),
              }}
            />
          )}
        </div>
      </div>

      <MenuOverlay isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </div>
  );
};

export default ProjectDetail;
